import type {PriceOutcome, Price} from '@/types/client'
import { Region, User, UserProfile } from '@/types/user'
import axios from 'axios'

import { environment } from '@/environments/environment'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'

const client = axios.create({
  baseURL:`${environment.API_CLIENT}`
})


// Request interceptor
client.interceptors.request.use(request => {
  console.log('[Axios] Request:', request.method?.toUpperCase(), request.url, request.data || '');
  console.log('Headers:', request.headers);
  return request;
}, error => {
  console.error('[Axios] Request error:', error);
  return Promise.reject(error);
});

// Response interceptor
client.interceptors.response.use(response => {
  console.log('[Axios] Response:', response.status, response.config.url);
  console.log('Data:', response.data);
  return response;
}, error => {
  if (error.response) {
    console.error('[Axios] Response error:', error.response.status, error.response.data);
  } else {
    console.error('[Axios] Network error:', error.message);
  }
  return Promise.reject(error);
});

function formatDecimal(decimal: number | null | undefined): string {
	if (decimal == null || !isFinite(decimal)) return 'N/A'
	return decimal.toFixed(2)
}

export function computeDecimalOdds(priceObj: Price | null | undefined): number {
	if (!priceObj) return -Infinity

	let decimal: number | null = null

	if (priceObj.decimal != null) {
		decimal = priceObj.decimal
	} else if (priceObj.traditional) {
		const {numerator, denominator} = priceObj.traditional
		if (numerator != null && denominator > 0) {
			decimal = numerator / denominator + 1
		}
	} else if (priceObj.formatted?.includes('/')) {
		const [numStr, denomStr] = priceObj.formatted.split('/')
		const numerator = parseFloat(numStr)
		const denominator = parseFloat(denomStr)
		if (!isNaN(numerator) && denominator > 0) {
			decimal = numerator / denominator + 1
		}
	}

	return decimal != null ? Math.round(decimal * 100) / 100 : -Infinity
}

export function isBestOverallPrice(
		bookmakers: PriceOutcome[],
		currentBookmaker: PriceOutcome
): boolean {
	const bestPrice = Math.max(
			...bookmakers.map(b => computeDecimalOdds(b.price)).filter(p => p > -Infinity)
	)

	const currentPrice = computeDecimalOdds(currentBookmaker.price)
	return Math.abs(currentPrice - bestPrice) < 0.0001
}


export function countMissingPrices(bookmaker: PriceOutcome) {
	let missingCount = 0
	bookmaker.legs?.forEach(leg => {
		leg.outcomes?.forEach(outcome => {
			if (!outcome.price?.formatted) {
				missingCount++
			}
		})
	})
	return missingCount
}

export function getPriceByPosition(bookmaker: PriceOutcome, market: string, resultText: string, eventName: string): string {
	const leg = bookmaker.legs?.find(leg =>
			leg.outcomes?.some(o => {
				const parts = o.description.split('|')
				return parts[0] === eventName && parts[2] === market && parts[1] === resultText
			})
	)
	if (!leg) return 'N/A'
	const outcome = leg.outcomes.find(o => {
		const parts = o.description.split('|')
		return parts[0] === eventName && parts[2] === market && parts[1] === resultText
	})
	if (!outcome?.price?.formatted) return 'N/A'
	return '@' + formatDecimal(outcome.price.decimal)
}

export function hasPriceByPosition(bookmaker: PriceOutcome, market: string, resultText: string, eventName: string): boolean {
	return !!bookmaker.legs?.some(leg =>
			leg.outcomes?.some(outcome => {
				const parts = outcome.description.split('|')
				return (
						parts[0] === eventName &&
						parts[2] === market &&
						parts[1] === resultText &&
						outcome.price?.formatted
				)
			})
	)
}

export function bestOddsByPosition(bookmakers: PriceOutcome[]) {
	const bestMap = new Map()

	bookmakers.forEach(bookmaker => {
		bookmaker.legs?.forEach(leg => {
			leg.outcomes?.forEach(outcome => {
				const parts = outcome.description.split('|')
				if (parts.length < 3) return

				const event = parts[0]
				const resultText = parts[1]
				const market = parts[2]
				const key = `${event}-${market}-${resultText}`

				const val = computeDecimalOdds(outcome.price)
				if (val != null && val > -Infinity) {
					const existing = bestMap.get(key)
					if (existing == null || val > existing) {
						bestMap.set(key, val)
					}
				}
			})
		})
	})

	return bestMap
}

export function isBestOddsByPosition(
		bestMap: Map<string, number>,
		market: string,
		resultText: string,
		eventName: string,
		bookmaker: PriceOutcome
): boolean {
	const key = `${eventName}-${market}-${resultText}`
	const bestValue = bestMap.get(key)
	if (bestValue == null) return false

	const leg = bookmaker.legs?.find(leg =>
			leg.outcomes?.some(o => {
				const parts = o.description.split('|')
				return parts[0] === eventName && parts[2] === market && parts[1] === resultText
			})
	)
	if (!leg) return false

	const outcome = leg.outcomes.find(o => {
		const parts = o.description.split('|')
		return parts[0] === eventName && parts[2] === market && parts[1] === resultText
	})
	if (!outcome?.price) return false

	const val = computeDecimalOdds(outcome.price)
	return Math.abs(val - bestValue) < 0.0001
}


interface Column {
	market: string
	resultText: string
	colKey: string
	raceNo?: string
}

export interface ColumnGroup {
	event: string
	columns: Column[]
}

export function groupColumns(bookmakers: PriceOutcome[]): ColumnGroup[] {
	if (!bookmakers.length) return []
	const eventGroupsMap = new Map<string, ColumnGroup>()

	bookmakers.forEach(bookmaker => {
		bookmaker.legs?.forEach(leg => {
			leg.outcomes?.forEach(outcome => {
				const parts = outcome.description.split('|')
				if (parts.length < 3) return

				const eventName = parts[0]
				const resultText = parts[1]
				const market = parts[2]
				const raceNo = parts.length === 4 ? parts[3] : undefined
				const colKey = `${eventName}-${market}-${resultText}`

				if (!eventGroupsMap.has(eventName)) {
					eventGroupsMap.set(eventName, {event: eventName, columns: []})
				}
				const group = eventGroupsMap.get(eventName)!

				if (!group.columns.find((c: Column) => c.colKey === colKey)) {
					group.columns.push({market, resultText, colKey, raceNo})
				}
			})
		})
	})

	return Array.from(eventGroupsMap.values())
}

export async function updateUserProfile(profile: UserProfile) {
	const headers: Record<string, string> = { 
		'Content-Type': 'application/json' 
	};
	if (Capacitor.isNativePlatform()) {
      const { value: token } = await Preferences.get({ key: 'jwt' })
      if (token) headers['X-JWT-Token'] = token
    }
  await client.put(`/user-profile/save/`, profile, {
    headers
  })
}

export async function updateUser(user: User) {
  const headers: Record<string, string> = { 
	'Content-Type': 'application/json' 
	};
  if (Capacitor.isNativePlatform()) {
	const { value: token } = await Preferences.get({ key: 'jwt' })
	if (token) headers['X-JWT-Token'] = token
  }
  await client.put(`/user/save/`, { userId: user.userId, username: user.username }, {
    headers
  })
}

export async function deleteUser(userId: number) {
	const headers: Record<string, string> = { 
		'Content-Type': 'application/json' 
	};
	if (Capacitor.isNativePlatform()) {
      const { value: token } = await Preferences.get({ key: 'jwt' })
      if (token) headers['X-JWT-Token'] = token
    }
  await client.post(`/user/delete/`, userId, {
    headers
  })
}

export async function unlinkProvider(provider: any) {
	const headers: Record<string, string> = { 
		'Content-Type': 'application/json' 
	};
	if (Capacitor.isNativePlatform()) {
		const { value: token } = await Preferences.get({ key: 'jwt' })
		if (token) headers['X-JWT-Token'] = token
	}
  await client.post(`/user/unlink/`, provider, {
    headers
  })
}

export async function getBookieRegions(): Promise<Region[]> {
	const res = await client.get(`/user-profile/bookieRegions`)
    return res.data
}
